/**
 * Qari Onboarding & Discovery — Phase 1 + onboarding-fields extension.
 *
 * Shapes mirror the columns defined in scripts/seed-qari.sql plus the
 * extension in scripts/seed-qari-fields.sql. We intentionally do NOT
 * regenerate `types.gen.ts` for these tables to keep the foundational
 * migrations reviewable independently.
 */

export type QariStatus =
  | 'draft'
  | 'pending_review'
  | 'under_verification'
  | 'approved'
  | 'rejected'
  | 'suspended'
  | 'inactive';

export const QARI_STATUS_LABELS: Record<QariStatus, string> = {
  draft: 'Draft',
  pending_review: 'Pending review',
  under_verification: 'Under verification',
  approved: 'Approved',
  rejected: 'Changes requested',
  suspended: 'Suspended',
  inactive: 'Inactive',
};

/**
 * Statuses where the qari can still edit their profile fields.
 * Status transitions are enforced by the database trigger; this is
 * just the UI hint.
 */
export const QARI_EDITABLE_STATUSES: ReadonlySet<QariStatus> = new Set([
  'draft',
  'rejected',
]);

export type QariHifzStatus = 'full' | 'partial' | 'none';

/**
 * Optional gender disclosure. PHASE2_ROADMAP Decision Log §3 says
 * "optional"; the screenshot UI marks it required. Schema allows null,
 * the wizard can enforce required at submit if you prefer.
 */
export type QariGender = 'male' | 'female' | 'prefer_not_to_say';

export const QARI_GENDER_LABELS: Record<QariGender, string> = {
  male: 'Male',
  female: 'Female',
  prefer_not_to_say: 'Prefer not to say',
};

/** Pedagogy style chips from the Expertise step. */
export type QariTeachingStyle =
  | 'traditional'
  | 'academic'
  | 'child_friendly'
  | 'accelerated'
  | 'foundational';

export const QARI_TEACHING_STYLE_LABELS: Record<QariTeachingStyle, string> = {
  traditional: 'Traditional',
  academic: 'Academic',
  child_friendly: 'Child-friendly',
  accelerated: 'Accelerated',
  foundational: 'Foundational',
};

/**
 * Language fluency vocabulary. The DB stores languages as a free
 * `text[]` (intentionally open so qaris can list languages we haven't
 * catalogued yet), but the UI presents these four as the primary set —
 * French dropped, Bengali added at the user's direction.
 */
export type QariLanguage = 'ar' | 'en' | 'ur' | 'bn';

export const QARI_LANGUAGE_LABELS: Record<QariLanguage, string> = {
  ar: 'Arabic',
  en: 'English',
  ur: 'Urdu',
  bn: 'Bengali',
};

export type QariDocumentKind =
  | 'ijazah'
  | 'identity'
  | 'certificate'
  | 'other';

export const QARI_DOCUMENT_KIND_LABELS: Record<QariDocumentKind, string> = {
  ijazah: 'Ijazah',
  identity: 'Identity',
  certificate: 'Certificate',
  other: 'Other',
};

export interface QariProfile {
  user_id: string;
  status: QariStatus;
  full_name: string | null;
  /** Public-facing short name. Added in seed-qari-fields.sql. */
  display_name: string | null;
  slug: string | null;
  bio: string | null;
  country: string | null;
  city: string | null;
  /** Sub-city granularity. Added in seed-qari-fields.sql. */
  district: string | null;
  languages: string[];
  years_experience: number | null;
  qiraat: string[];
  specializations: string[];
  /** Single headline discipline. Added in seed-qari-fields.sql. */
  primary_discipline: string | null;
  /** Pedagogy chips. Added in seed-qari-fields.sql. */
  teaching_styles: QariTeachingStyle[];
  /** Online / in-person / hybrid. View-referenced, defensively added. */
  teaching_modes: string[];
  /**
   * Audience descriptor (freeform). Stored as a single text column in
   * the DB — the earlier array-typed shape was a guess that didn't
   * match `qari_profiles.teaches`'s actual `text` type. Kept nullable
   * for parity with the column default.
   */
  teaches: string | null;
  hifz_status: QariHifzStatus | null;
  /** Optional disclosure. View-referenced, defensively added. */
  gender: QariGender | null;
  /** Verified-tier signal. View-referenced, defensively added. */
  sanad_certified: boolean;
  /** Avatar URL. View-referenced, defensively added. */
  profile_image_url: string | null;
  /** Optional age disclosure. View-referenced, defensively added. */
  age: number | null;
  contact_email: string | null;
  contact_phone: string | null;
  /**
   * Free-text certifications. ADMIN-PRIVATE per the onboarding UI's
   * privacy note. Omitted from `qari_profiles_public`. Column-level
   * grants will lock this down to the admin role in Phase 2 Slice 1.
   */
  certifications: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  approved_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Fields the qari can author. The Phase-1 wizard writes the first
 * 11 fields. The new fields from seed-qari-fields.sql are added here
 * as optional so the existing wizard's `toDraft` still type-checks —
 * extending the wizard to write them is a separate, incremental task.
 */
export type QariProfileDraft = Pick<
  QariProfile,
  | 'full_name'
  | 'bio'
  | 'country'
  | 'city'
  | 'languages'
  | 'years_experience'
  | 'qiraat'
  | 'specializations'
  | 'hifz_status'
  | 'contact_email'
  | 'contact_phone'
> &
  Partial<
    Pick<
      QariProfile,
      | 'display_name'
      | 'district'
      | 'primary_discipline'
      | 'teaching_styles'
      | 'teaching_modes'
      | 'teaches'
      | 'gender'
      | 'sanad_certified'
      | 'profile_image_url'
      | 'age'
      | 'certifications'
    >
  >;

export interface QariDocument {
  id: string;
  qari_user_id: string;
  kind: QariDocumentKind;
  storage_path: string;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  notes: string | null;
  created_at: string;
}

export interface QariVerificationLog {
  id: string;
  qari_user_id: string;
  from_status: QariStatus | null;
  to_status: QariStatus;
  actor_user_id: string | null;
  note: string | null;
  created_at: string;
}
